import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Check, Sparkles, ArrowRight, ArrowLeft, CreditCard,
    User, Briefcase, Search as SearchIcon, Building2, MapPin,
    Mail, Phone, Lock, Globe, Camera, Upload, X, Loader2,
    CheckCircle2, AlertCircle, Eye, EyeOff, Home, Smartphone, Settings
} from 'lucide-react';
import Header from '../public/Header';
import Footer from '../public/Footer';
import { AddressAutocomplete, CustomDropdown } from '../ui';
import { createPayment } from '../../services/paymentService';
import { searchByVatNumber, searchByName } from '../../services/cbeService';
// import { searchBySiren } from '../../services/sireneService'; // Désactivé pour l'instant
import { createPeople, createPlace, checkEmailExists, checkDuplicateProfile } from '../../services/apiService';
import { getTarifsByProfileType, getModulesByProfileTypeAndSubscription } from '../../services/tarifsService';
import { encryptPassword, validatePassword } from '../../utils/encryption';
import { useOCR } from '../../hooks/useOCR';
import { parsePhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useLanguage } from '../../context/LanguageContext';

// European countries with their company types
const EUROPEAN_COUNTRIES = {
    BE: {
        name: 'Belgique',
        flag: '🇧🇪',
        types: [
            { id: 'personne_physique', label: 'Personne physique' },
            { id: 'srl', label: 'SRL (Société à Responsabilité Limitée)' },
            { id: 'sa', label: 'SA (Société Anonyme)' },
            { id: 'sc', label: 'SC (Société Coopérative)' },
            { id: 'snc', label: 'SNC (Société en Nom Collectif)' },
            { id: 'scs', label: 'SCS (Société en Commandite Simple)' },
            { id: 'scomm', label: 'SComm (Société en Commandite)' },
            { id: 'asbl', label: 'ASBL' },
            { id: 'aisbl', label: 'AISBL' },
            { id: 'fondation', label: 'Fondation' },
            { id: 'association_fait', label: 'Association de fait' }
        ]
    },
    FR: {
        name: 'France',
        flag: '🇫🇷',
        types: [
            { id: 'auto_entrepreneur', label: 'Auto-entrepreneur / Micro-entreprise' },
            { id: 'ei', label: 'EI (Entreprise Individuelle)' },
            { id: 'eurl', label: 'EURL' },
            { id: 'sarl', label: 'SARL' },
            { id: 'sas', label: 'SAS' },
            { id: 'sasu', label: 'SASU' },
            { id: 'sa', label: 'SA (Société Anonyme)' },
            { id: 'snc', label: 'SNC' },
            { id: 'sci', label: 'SCI (Société Civile Immobilière)' },
            { id: 'scop', label: 'SCOP' },
            { id: 'association', label: 'Association loi 1901' }
        ]
    },
    LU: {
        name: 'Luxembourg',
        flag: '🇱🇺',
        types: [
            { id: 'personne_physique', label: 'Personne physique' },
            { id: 'sarl', label: 'SARL' },
            { id: 'sa', label: 'SA' },
            { id: 'sca', label: 'SCA (Société en Commandite par Actions)' },
            { id: 'scs', label: 'SCS' },
            { id: 'snc', label: 'SNC' },
            { id: 'asbl', label: 'ASBL' },
            { id: 'fondation', label: 'Fondation' }
        ]
    },
    NL: {
        name: 'Pays-Bas',
        flag: '🇳🇱',
        types: [
            { id: 'eenmanszaak', label: 'Eenmanszaak (Entreprise individuelle)' },
            { id: 'bv', label: 'BV (Besloten Vennootschap)' },
            { id: 'nv', label: 'NV (Naamloze Vennootschap)' },
            { id: 'vof', label: 'VOF (Vennootschap onder Firma)' },
            { id: 'cv', label: 'CV (Commanditaire Vennootschap)' },
            { id: 'stichting', label: 'Stichting (Fondation)' },
            { id: 'vereniging', label: 'Vereniging (Association)' }
        ]
    },
    DE: {
        name: 'Allemagne',
        flag: '🇩🇪',
        types: [
            { id: 'einzelunternehmen', label: 'Einzelunternehmen (Entreprise individuelle)' },
            { id: 'gmbh', label: 'GmbH' },
            { id: 'ug', label: 'UG (haftungsbeschränkt)' },
            { id: 'ag', label: 'AG (Aktiengesellschaft)' },
            { id: 'ohg', label: 'OHG' },
            { id: 'kg', label: 'KG (Kommanditgesellschaft)' },
            { id: 'gbr', label: 'GbR' },
            { id: 'ev', label: 'e.V. (Verein)' },
            { id: 'stiftung', label: 'Stiftung (Fondation)' }
        ]
    },
    GB: {
        name: 'Royaume-Uni',
        flag: '🇬🇧',
        types: [
            { id: 'sole_trader', label: 'Sole Trader' },
            { id: 'ltd', label: 'Ltd (Private Limited Company)' },
            { id: 'plc', label: 'PLC (Public Limited Company)' },
            { id: 'llp', label: 'LLP (Limited Liability Partnership)' },
            { id: 'partnership', label: 'Partnership' },
            { id: 'cic', label: 'CIC (Community Interest Company)' },
            { id: 'charity', label: 'Charity' }
        ]
    },
    ES: {
        name: 'Espagne',
        flag: '🇪🇸',
        types: [
            { id: 'autonomo', label: 'Autónomo' },
            { id: 'sl', label: 'SL (Sociedad Limitada)' },
            { id: 'sa', label: 'SA (Sociedad Anónima)' },
            { id: 'sc', label: 'SC (Sociedad Civil)' },
            { id: 'cb', label: 'CB (Comunidad de Bienes)' },
            { id: 'cooperativa', label: 'Cooperativa' },
            { id: 'asociacion', label: 'Asociación' },
            { id: 'fundacion', label: 'Fundación' }
        ]
    },
    IT: {
        name: 'Italie',
        flag: '🇮🇹',
        types: [
            { id: 'ditta_individuale', label: 'Ditta Individuale' },
            { id: 'srl', label: 'SRL (Società a Responsabilità Limitata)' },
            { id: 'srls', label: 'SRLS (SRL Semplificata)' },
            { id: 'spa', label: 'SpA (Società per Azioni)' },
            { id: 'snc', label: 'SNC' },
            { id: 'sas', label: 'SAS' },
            { id: 'ss', label: 'SS (Società Semplice)' },
            { id: 'cooperativa', label: 'Cooperativa' },
            { id: 'associazione', label: 'Associazione' }
        ]
    },
    PT: {
        name: 'Portugal',
        flag: '🇵🇹',
        types: [
            { id: 'empresario_individual', label: 'Empresário em Nome Individual' },
            { id: 'lda', label: 'Lda (Sociedade por Quotas)' },
            { id: 'sa', label: 'SA (Sociedade Anónima)' },
            { id: 'unipessoal', label: 'Sociedade Unipessoal' },
            { id: 'cooperativa', label: 'Cooperativa' },
            { id: 'associacao', label: 'Associação' }
        ]
    },
    AT: {
        name: 'Autriche',
        flag: '🇦🇹',
        types: [
            { id: 'einzelunternehmen', label: 'Einzelunternehmen' },
            { id: 'gmbh', label: 'GmbH' },
            { id: 'ag', label: 'AG' },
            { id: 'og', label: 'OG (Offene Gesellschaft)' },
            { id: 'kg', label: 'KG' },
            { id: 'gesnbr', label: 'GesbR' },
            { id: 'verein', label: 'Verein' }
        ]
    },
    CH: {
        name: 'Suisse',
        flag: '🇨🇭',
        types: [
            { id: 'raison_individuelle', label: 'Raison individuelle' },
            { id: 'sarl', label: 'Sàrl / GmbH' },
            { id: 'sa', label: 'SA / AG' },
            { id: 'snc', label: 'SNC' },
            { id: 'sc', label: 'Société en commandite' },
            { id: 'association', label: 'Association' },
            { id: 'fondation', label: 'Fondation' }
        ]
    },
    PL: {
        name: 'Pologne',
        flag: '🇵🇱',
        types: [
            { id: 'jdg', label: 'JDG (Działalność gospodarcza)' },
            { id: 'sp_z_oo', label: 'Sp. z o.o.' },
            { id: 'sa', label: 'SA (Spółka akcyjna)' },
            { id: 'sj', label: 'Spółka jawna' },
            { id: 'sk', label: 'Spółka komandytowa' },
            { id: 'stowarzyszenie', label: 'Stowarzyszenie' },
            { id: 'fundacja', label: 'Fundacja' }
        ]
    },
    IE: {
        name: 'Irlande',
        flag: '🇮🇪',
        types: [
            { id: 'sole_trader', label: 'Sole Trader' },
            { id: 'ltd', label: 'Ltd (Private Company Limited)' },
            { id: 'dac', label: 'DAC (Designated Activity Company)' },
            { id: 'plc', label: 'PLC' },
            { id: 'partnership', label: 'Partnership' },
            { id: 'clg', label: 'CLG (Company Limited by Guarantee)' }
        ]
    },
    SE: {
        name: 'Suède',
        flag: '🇸🇪',
        types: [
            { id: 'enskild_firma', label: 'Enskild firma' },
            { id: 'ab', label: 'AB (Aktiebolag)' },
            { id: 'hb', label: 'HB (Handelsbolag)' },
            { id: 'kb', label: 'KB (Kommanditbolag)' },
            { id: 'ek_for', label: 'Ekonomisk förening' },
            { id: 'stiftelse', label: 'Stiftelse' }
        ]
    },
    DK: {
        name: 'Danemark',
        flag: '🇩🇰',
        types: [
            { id: 'enkeltmandsvirksomhed', label: 'Enkeltmandsvirksomhed' },
            { id: 'aps', label: 'ApS (Anpartsselskab)' },
            { id: 'as', label: 'A/S (Aktieselskab)' },
            { id: 'is', label: 'I/S (Interessentskab)' },
            { id: 'ks', label: 'K/S (Kommanditselskab)' },
            { id: 'forening', label: 'Forening' }
        ]
    },
    FI: {
        name: 'Finlande',
        flag: '🇫🇮',
        types: [
            { id: 'toiminimi', label: 'Toiminimi' },
            { id: 'oy', label: 'Oy (Osakeyhtiö)' },
            { id: 'oyj', label: 'Oyj (Public)' },
            { id: 'ky', label: 'Ky (Kommandiittiyhtiö)' },
            { id: 'ay', label: 'Ay (Avoin yhtiö)' },
            { id: 'osuuskunta', label: 'Osuuskunta' }
        ]
    },
    NO: {
        name: 'Norvège',
        flag: '🇳🇴',
        types: [
            { id: 'enkeltpersonforetak', label: 'Enkeltpersonforetak' },
            { id: 'as', label: 'AS (Aksjeselskap)' },
            { id: 'asa', label: 'ASA (Allmennaksjeselskap)' },
            { id: 'ans', label: 'ANS' },
            { id: 'da', label: 'DA' },
            { id: 'ks', label: 'KS' }
        ]
    },
    CZ: {
        name: 'Tchéquie',
        flag: '🇨🇿',
        types: [
            { id: 'osvc', label: 'OSVČ (Indépendant)' },
            { id: 'sro', label: 's.r.o.' },
            { id: 'as', label: 'a.s.' },
            { id: 'vos', label: 'v.o.s.' },
            { id: 'ks', label: 'k.s.' },
            { id: 'druzstvo', label: 'Družstvo' }
        ]
    },
    GR: {
        name: 'Grèce',
        flag: '🇬🇷',
        types: [
            { id: 'atomiki', label: 'Ατομική Επιχείρηση' },
            { id: 'epe', label: 'ΕΠΕ' },
            { id: 'ike', label: 'ΙΚΕ' },
            { id: 'ae', label: 'ΑΕ' },
            { id: 'oe', label: 'ΟΕ' },
            { id: 'ee', label: 'ΕΕ' }
        ]
    },
    RO: {
        name: 'Roumanie',
        flag: '🇷🇴',
        types: [
            { id: 'pfa', label: 'PFA' },
            { id: 'srl', label: 'SRL' },
            { id: 'sa', label: 'SA' },
            { id: 'snc', label: 'SNC' },
            { id: 'scs', label: 'SCS' },
            { id: 'ong', label: 'ONG' }
        ]
    },
    HU: {
        name: 'Hongrie',
        flag: '🇭🇺',
        types: [
            { id: 'egyeni_vallalkozo', label: 'Egyéni vállalkozó' },
            { id: 'kft', label: 'Kft.' },
            { id: 'zrt', label: 'Zrt.' },
            { id: 'nyrt', label: 'Nyrt.' },
            { id: 'bt', label: 'Bt.' },
            { id: 'kkt', label: 'Kkt.' }
        ]
    },
    BG: {
        name: 'Bulgarie',
        flag: '🇧🇬',
        types: [
            { id: 'et', label: 'ЕТ (Едноличен търговец)' },
            { id: 'eood', label: 'ЕООД' },
            { id: 'ood', label: 'ООД' },
            { id: 'ad', label: 'АД' },
            { id: 'sd', label: 'СД' },
            { id: 'kd', label: 'КД' }
        ]
    },
    HR: {
        name: 'Croatie',
        flag: '🇭🇷',
        types: [
            { id: 'obrt', label: 'Obrt' },
            { id: 'doo', label: 'd.o.o.' },
            { id: 'dd', label: 'd.d.' },
            { id: 'jdoo', label: 'j.d.o.o.' },
            { id: 'udruga', label: 'Udruga' }
        ]
    },
    SK: {
        name: 'Slovaquie',
        flag: '🇸🇰',
        types: [
            { id: 'szco', label: 'SZČO' },
            { id: 'sro', label: 's.r.o.' },
            { id: 'as', label: 'a.s.' },
            { id: 'vos', label: 'v.o.s.' },
            { id: 'ks', label: 'k.s.' }
        ]
    },
    SI: {
        name: 'Slovénie',
        flag: '🇸🇮',
        types: [
            { id: 'sp', label: 's.p.' },
            { id: 'doo', label: 'd.o.o.' },
            { id: 'dd', label: 'd.d.' },
            { id: 'dnoo', label: 'd.n.o.' },
            { id: 'kd', label: 'k.d.' }
        ]
    },
    EE: {
        name: 'Estonie',
        flag: '🇪🇪',
        types: [
            { id: 'fie', label: 'FIE' },
            { id: 'ou', label: 'OÜ' },
            { id: 'as', label: 'AS' },
            { id: 'tu', label: 'TÜ' },
            { id: 'uy', label: 'UÜ' }
        ]
    },
    LV: {
        name: 'Lettonie',
        flag: '🇱🇻',
        types: [
            { id: 'individualis', label: 'Individuālais komersants' },
            { id: 'sia', label: 'SIA' },
            { id: 'as', label: 'AS' },
            { id: 'ps', label: 'PS' },
            { id: 'ks', label: 'KS' }
        ]
    },
    LT: {
        name: 'Lituanie',
        flag: '🇱🇹',
        types: [
            { id: 'ii', label: 'IĮ (Individuali įmonė)' },
            { id: 'uab', label: 'UAB' },
            { id: 'ab', label: 'AB' },
            { id: 'tj', label: 'TŪB' },
            { id: 'kub', label: 'KŪB' }
        ]
    },
    CY: {
        name: 'Chypre',
        flag: '🇨🇾',
        types: [
            { id: 'sole_proprietorship', label: 'Sole Proprietorship' },
            { id: 'ltd', label: 'Ltd' },
            { id: 'plc', label: 'PLC' },
            { id: 'partnership', label: 'Partnership' }
        ]
    },
    MT: {
        name: 'Malte',
        flag: '🇲🇹',
        types: [
            { id: 'sole_trader', label: 'Sole Trader' },
            { id: 'ltd', label: 'Ltd' },
            { id: 'plc', label: 'PLC' },
            { id: 'partnership', label: 'Partnership' }
        ]
    }
};

// Default profile types - will be replaced by API data
const DEFAULT_PROFILE_TYPES = [
    {
        id: 'STUDENT',
        dbId: 1,
        code: 'STUDENT',
        icon: User,
        title: 'Étudiant',
        titleEn: 'Student',
        description: 'Je suis étudiant(e)',
        color: 'from-cyan-500 to-cyan-600',
        bgColor: 'bg-cyan-50',
        borderColor: 'border-cyan-500',
        isProfessional: false,
        features: ['Gestion de contacts', 'Réseau académique', 'Scan de cartes']
    },
    {
        id: 'INDIVIDUAL',
        dbId: 3,
        code: 'INDIVIDUAL',
        icon: Home,
        title: 'Particulier',
        titleEn: 'Individual',
        description: 'Je souhaite gérer mes contacts personnels',
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-500',
        isProfessional: false,
        features: ['Gestion de contacts', 'Carnet d\'adresses', 'Scan de cartes']
    },
    {
        id: 'ARTISAN',
        dbId: 4,
        code: 'ARTISAN',
        icon: Building2,
        title: 'Artisan',
        titleEn: 'Artisan',
        description: 'Je suis artisan indépendant',
        color: 'from-orange-500 to-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-500',
        isProfessional: true,
        features: ['Gestion clients', 'Fiche entreprise', 'Scan de cartes']
    },
    {
        id: 'PROFESSIONAL',
        dbId: 5,
        code: 'PROFESSIONAL',
        icon: Briefcase,
        title: 'Professionnel',
        titleEn: 'Professional',
        description: 'Je représente une entreprise',
        color: 'from-cabaza-purple to-cabaza-pink',
        bgColor: 'bg-purple-50',
        borderColor: 'border-cabaza-purple',
        isProfessional: true,
        features: ['Gestion clients', 'Fiche entreprise', 'CRM intégré']
    },
    {
        id: 'RETIRED',
        dbId: 6,
        code: 'RETIRED',
        icon: User,
        title: 'Pensionné',
        titleEn: 'Retired',
        description: 'Je suis retraité(e)',
        color: 'from-gray-500 to-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-500',
        isProfessional: false,
        features: ['Gestion de contacts', 'Carnet d\'adresses', 'Scan de cartes']
    }
];

// Steps are now generated dynamically in the component to use translations

const RegisterPage = ({ onLogin }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();

    // Steps with translations
    const STEPS = [
        { id: 'profile', title: t.register?.steps?.profile || 'Type de profil', icon: User },
        { id: 'personal', title: t.register?.steps?.personal || 'Informations personnelles', icon: Mail },
        { id: 'professional', title: t.register?.steps?.professional || 'Entreprise', icon: Building2, professionalOnly: true },
        { id: 'address', title: t.register?.steps?.address || 'Localisation', icon: MapPin },
        { id: 'privacy', title: t.register?.steps?.privacy || 'Confidentialité', icon: Lock },
        { id: 'plan', title: t.register?.steps?.plan || 'Abonnement', icon: CreditCard }
    ];

    // Wizard state
    const [currentStep, setCurrentStep] = useState(0);
    const [profileType, setProfileType] = useState(null);
    const [profileTypeId, setProfileTypeId] = useState(null);
    const [profileTypes, setProfileTypes] = useState(DEFAULT_PROFILE_TYPES);
    const [employmentStatus, setEmploymentStatus] = useState(null); // 'JOBSEEKER', 'EMPLOYER', or null
    const [selectedPlan, setSelectedPlan] = useState(location.state?.selectedPlan || 'free');
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);

    // Employment status options
    const EMPLOYMENT_STATUS_OPTIONS = [
        { id: 'EMPLOYER', label: t.register?.employmentStatus?.employer || 'Offreur d\'emploi', icon: Briefcase, color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-500' },
        { id: 'JOBSEEKER', label: t.register?.employmentStatus?.jobseeker || 'Demandeur d\'emploi', icon: SearchIcon, color: 'from-green-500 to-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-500' }
    ];

    // Form data
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        sexe: '',           // Genre (M/F/O)
        email: '',
        phone: '',          // Téléphone fixe
        gsm: '',            // Téléphone mobile
        phoneBureau: '',    // Téléphone bureau
        password: '',
        confirmPassword: '',
        company: '',
        position: '',       // Fonction dans l'entreprise
        profession: '',     // Métier/titre professionnel
        vatNumber: '',
        address: '',
        street: '',
        number: '',
        box: '',            // Boîte
        zip: '',            // Code postal (alias postalCode)
        city: '',
        country: '',        // Pays
        countryCode: '',    // Code pays (BE, FR, etc.)
        latitude: null,
        longitude: null,
        googlePlaceId: '',  // ID Google Places
        googleUrl: '',      // URL Google Maps
        nationality: '',    // Nationalité
        website: '',
        description: '',    // Bio/description
        role: 'PRIVATE',
        // Professional fields
        companyCountry: 'BE',
        firmType: '',
        companyAddress: '', // googleAddress for the company/place
        // Privacy settings
        showPhone: false,
        showGsm: false,
        showEmail: true
    });

    // UI state
    const [showPassword, setShowPassword] = useState(false);
    const [showOcrModal, setShowOcrModal] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createdUser, setCreatedUser] = useState(null);

    // BCE search state (Belgium only for now)
    const [bceSearchMode, setBceSearchMode] = useState('vat'); // 'vat' or 'name'
    const [vatSearching, setVatSearching] = useState(false);
    const [vatSearchResult, setVatSearchResult] = useState(null);
    const [vatError, setVatError] = useState(null);

    // BCE search by name
    const [nameSearchQuery, setNameSearchQuery] = useState('');
    const [nameSearchResults, setNameSearchResults] = useState([]);
    const [nameSearching, setNameSearching] = useState(false);
    const [selectedBceResult, setSelectedBceResult] = useState(null);

    // OCR
    const { isProcessing: ocrProcessing, imagePreview, processImage, clearImage } = useOCR('fr');
    const [businessCardFile, setBusinessCardFile] = useState(null);
    const [ocrExtractedData, setOcrExtractedData] = useState(null);
    const [showOcrConfirmModal, setShowOcrConfirmModal] = useState(false);

    // BCE confirmation modal
    const [showBceConfirmModal, setShowBceConfirmModal] = useState(false);
    const [bceDataToApply, setBceDataToApply] = useState(null);

    // Email already exists modal
    const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);

    // Google Maps API Key
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    // Load profile types from API
    useEffect(() => {
        const loadProfileTypes = async () => {
            try {
                const response = await fetch('https://apicabaza.creago.be/rest/cabaza/peoples/profile-types?lang=fr');
                const result = await response.json();
                if (result.success && result.data?.length > 0) {
                    // Merge API data with default visual config
                    const mergedTypes = result.data.map(apiType => {
                        const defaultType = DEFAULT_PROFILE_TYPES.find(d => d.code === apiType.code);
                        return {
                            ...defaultType,
                            id: apiType.id,
                            code: apiType.code,
                            title: apiType.name_fr || defaultType?.title || apiType.code,
                            titleEn: apiType.name_en || defaultType?.titleEn || apiType.code,
                            dbId: apiType.id, // Store the database ID
                            // Ensure features is always an array
                            features: defaultType?.features || [],
                            description: defaultType?.description || '',
                            color: defaultType?.color || 'from-gray-500 to-gray-600',
                            bgColor: defaultType?.bgColor || 'bg-gray-50',
                            borderColor: defaultType?.borderColor || 'border-gray-500',
                            icon: defaultType?.icon || User
                        };
                    });
                    setProfileTypes(mergedTypes);
                }
            } catch (error) {
                console.error('Failed to load profile types:', error);
                // Keep default types if API fails
            }
        };
        loadProfileTypes();
    }, []);

    // Update privacy defaults when profile type or employment status changes
    useEffect(() => {
        if (!profileType) return;
        // Artisan (4) and Professional (5) = all checked
        const isPro = profileType === 'ARTISAN' || profileType === 'PROFESSIONAL';
        // Jobseekers also get email-only defaults
        const isJobseeker = employmentStatus === 'JOBSEEKER';
        if (isPro && !isJobseeker) {
            setFormData(prev => ({ ...prev, showPhone: true, showGsm: true, showEmail: true }));
        } else {
            // Students, Individuals, Retired, Jobseekers = email only
            setFormData(prev => ({ ...prev, showPhone: false, showGsm: false, showEmail: true }));
        }
    }, [profileType, employmentStatus]);

    // Plans - loaded from API
    const [tarifs, setTarifs] = useState([]);
    const [tarifModules, setTarifModules] = useState({});
    const [loadingTarifs, setLoadingTarifs] = useState(false);

    // Load tarifs when profile type changes
    useEffect(() => {
        const loadTarifs = async () => {
            if (!profileTypeId) return;

            setLoadingTarifs(true);
            try {
                // Load tarifs for this profile type
                const tarifsData = await getTarifsByProfileType(profileTypeId);
                if (tarifsData && tarifsData.length > 0) {
                    setTarifs(tarifsData);

                    // Load modules for each tarif
                    const modulesData = await getModulesByProfileTypeAndSubscription(profileTypeId, '');
                    if (modulesData && modulesData.length > 0) {
                        // Group modules by subscription type
                        const grouped = {};
                        modulesData.forEach(mod => {
                            const subType = (mod.subscriptiontype || mod.subscriptionType || 'FREE').toLowerCase();
                            if (!grouped[subType]) grouped[subType] = [];
                            grouped[subType].push(mod);
                        });
                        setTarifModules(grouped);
                    }

                    // Set default plan to free
                    const freeTarif = tarifsData.find(t => t.code?.toLowerCase() === 'free');
                    if (freeTarif && selectedPlan === 'free') {
                        setSelectedPlan('free');
                    }
                }
            } catch (error) {
                console.error('Failed to load tarifs:', error);
            } finally {
                setLoadingTarifs(false);
            }
        };
        loadTarifs();
    }, [profileTypeId]);

    // Get current language
    const { language } = useLanguage();

    // Convert phone to E.164
    const convertToE164 = (phone, defaultCountry = 'BE') => {
        if (!phone) return '';
        try {
            const phoneNumber = parsePhoneNumber(phone, defaultCountry);
            if (phoneNumber && phoneNumber.isValid()) {
                return phoneNumber.format('E.164');
            }
        } catch (e) {
            console.warn('Could not parse phone:', phone);
        }
        return phone;
    };

    // Handle form field changes
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear field error when user types
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    // Handle OCR image upload
    const handleOcrUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setBusinessCardFile(file);
        const extractedData = await processImage(file);

        if (extractedData) {
            // Store extracted data and show confirmation modal
            setOcrExtractedData({
                firstName: extractedData.firstName || '',
                lastName: extractedData.lastName || '',
                email: extractedData.email || '',
                phone: extractedData.phone ? convertToE164(extractedData.phone) : '',
                company: extractedData.company || '',
                position: extractedData.position || '',
                website: extractedData.website || '',
                address: extractedData.address || ''
            });
            setShowOcrModal(false);
            setShowOcrConfirmModal(true);
        }
    };

    // Apply OCR data after confirmation
    const applyOcrData = () => {
        if (ocrExtractedData) {
            setFormData(prev => ({
                ...prev,
                firstName: ocrExtractedData.firstName || prev.firstName,
                lastName: ocrExtractedData.lastName || prev.lastName,
                email: ocrExtractedData.email || prev.email,
                phone: ocrExtractedData.phone || prev.phone,
                company: ocrExtractedData.company || prev.company,
                position: ocrExtractedData.position || prev.position,
                website: ocrExtractedData.website || prev.website,
                address: ocrExtractedData.address || prev.address
            }));
        }
        setShowOcrConfirmModal(false);
        setOcrExtractedData(null);
        clearImage();
        // Go to next step after applying
        nextStep();
    };

    // Cancel OCR data application
    const cancelOcrData = () => {
        setShowOcrConfirmModal(false);
        setOcrExtractedData(null);
        clearImage();
    };

    // Search BCE by VAT number (Belgium only)
    const handleVatSearch = async () => {
        const searchValue = formData.vatNumber?.trim();

        if (!searchValue || searchValue.replace(/\D/g, '').length < 9) {
            setVatError(t.register?.validation?.vatInvalid || 'Veuillez entrer un numéro d\'entreprise valide (9-10 chiffres)');
            return;
        }

        setVatSearching(true);
        setVatError(null);
        setVatSearchResult(null);

        const result = await searchByVatNumber(searchValue);

        if (result?.success && result.data) {
            setVatSearchResult(result.data);
            // Store data to apply after confirmation
            setBceDataToApply(result.data);
        } else {
            setVatError(result?.message || t.register?.errors?.companyNotFound || 'Entreprise non trouvée');
        }

        setVatSearching(false);
    };

    // Search BCE by company name (Belgium only)
    const handleNameSearch = async () => {
        const query = nameSearchQuery?.trim();

        if (!query || query.length < 2) {
            setVatError(t.register?.validation?.minChars || 'Veuillez entrer au moins 2 caractères');
            return;
        }

        setNameSearching(true);
        setVatError(null);
        setNameSearchResults([]);
        setSelectedBceResult(null);

        const result = await searchByName(query);

        if (result?.success && result.data?.length > 0) {
            setNameSearchResults(result.data);
        } else if (result?.success && result.data?.length === 0) {
            setVatError(t.register?.errors?.noCompanyFound || 'Aucune entreprise trouvée');
        } else {
            setVatError(result?.message || t.register?.errors?.searchError || 'Erreur lors de la recherche');
        }

        setNameSearching(false);
    };

    // Select a BCE result from name search
    const handleSelectBceResult = (result) => {
        setSelectedBceResult(result);
        setVatSearchResult(result);
        setBceDataToApply(result);
        setNameSearchResults([]);
    };

    // Apply BCE data after confirmation
    const applyBceData = () => {
        if (bceDataToApply) {
            setFormData(prev => ({
                ...prev,
                company: bceDataToApply.placeName || prev.company,
                vatNumber: bceDataToApply.tvaNum || prev.vatNumber,
                firmType: bceDataToApply.firmType || prev.firmType,
                companyAddress: bceDataToApply.googleAddress || prev.companyAddress,
                phone: bceDataToApply.phone || prev.phone,
                website: bceDataToApply.website || prev.website,
                // Also fill personal address with company address if empty
                address: prev.address || bceDataToApply.googleAddress || ''
            }));
        }
        setShowBceConfirmModal(false);
        setBceDataToApply(null);
        // Go to next step after applying
        nextStep();
    };

    // Cancel BCE data application
    const cancelBceData = () => {
        setShowBceConfirmModal(false);
        setBceDataToApply(null);
        setVatSearchResult(null);
    };

    // Handle address selection from autocomplete
    const handleAddressChange = (field, value) => {
        setFormData(prev => ({ ...prev, address: value }));
    };

    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    };

    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            street: components.street || prev.street,
            number: components.number || prev.number,
            zip: components.zip || components.postalCode || prev.zip,
            city: components.city || prev.city,
            country: components.country || prev.country,
            countryCode: components.countryCode || prev.countryCode,
            googlePlaceId: components.placeId || prev.googlePlaceId,
            googleUrl: components.url || prev.googleUrl
        }));
    };

    // State for validation loading
    const [isValidating, setIsValidating] = useState(false);

    // Validate current step (async for API checks)
    const validateStep = async () => {
        const errors = {};
        const visibleSteps = getVisibleSteps();
        const currentVisibleStep = visibleSteps[currentStep];

        if (!currentVisibleStep) return true;

        switch (currentVisibleStep.id) {
            case 'profile': // Profile type
                if (!profileType || !profileTypeId) {
                    setError(t.register?.validation?.selectProfileType || 'Veuillez sélectionner un type de profil');
                    return false;
                }
                break;
            case 'personal': // Personal info
                if (!formData.firstName.trim()) errors.firstName = t.register?.validation?.firstNameRequired || 'Prénom requis';
                if (!formData.lastName.trim()) errors.lastName = t.register?.validation?.lastNameRequired || 'Nom requis';
                if (!formData.email.trim()) {
                    errors.email = t.register?.validation?.emailRequired || 'Email requis';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                    errors.email = t.register?.validation?.emailInvalid || 'Email invalide';
                }
                if (!formData.password) {
                    errors.password = t.register?.validation?.passwordRequired || 'Mot de passe requis';
                } else if (formData.password.length < 6) {
                    errors.password = t.register?.validation?.passwordMinLength || 'Minimum 6 caractères';
                }
                if (formData.password !== formData.confirmPassword) {
                    errors.confirmPassword = t.register?.validation?.passwordMismatch || 'Les mots de passe ne correspondent pas';
                }

                // If basic validation passes, check if email already exists
                if (Object.keys(errors).length === 0 && formData.email.trim()) {
                    setIsValidating(true);
                    try {
                        const emailCheck = await checkEmailExists(formData.email.trim());
                        if (emailCheck.exists) {
                            errors.email = t.register?.validation?.emailExists || 'Cette adresse email est déjà associée à un compte';
                        }
                    } catch (err) {
                        console.error('Error checking email:', err);
                    } finally {
                        setIsValidating(false);
                    }
                }
                break;
            case 'professional': // Professional info
                if (!formData.company.trim()) {
                    errors.company = t.register?.validation?.companyRequired || 'Nom de l\'entreprise requis';
                }
                break;
            case 'address': // Address - check for duplicate profile
                // Check for duplicate profile if we have name + location
                if (formData.firstName.trim() && formData.lastName.trim() && (formData.zip.trim() || formData.city.trim())) {
                    setIsValidating(true);
                    try {
                        const duplicateCheck = await checkDuplicateProfile(
                            formData.firstName.trim(),
                            formData.lastName.trim(),
                            formData.zip.trim(),
                            formData.city.trim()
                        );
                        if (duplicateCheck.exists) {
                            setError(t.register?.validation?.profileExists || 'Un profil avec ces informations existe déjà');
                            setIsValidating(false);
                            return false;
                        }
                    } catch (err) {
                        console.error('Error checking duplicate profile:', err);
                    } finally {
                        setIsValidating(false);
                    }
                }
                break;
            case 'plan': // Plan
                break;
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Get visible steps helper for validateStep (need to define before validateStep uses it)
    const isProfessionalProfile = () => {
        const selectedType = profileTypes.find(t => t.code === profileType);
        return selectedType?.isProfessional || false;
    };

    const getVisibleSteps = () => {
        return STEPS.filter(step => !step.professionalOnly || isProfessionalProfile());
    };

    // Navigate steps
    const nextStep = async () => {
        const isValid = await validateStep();
        if (isValid) {
            setError(null);
            const visibleSteps = getVisibleSteps();
            if (currentStep < visibleSteps.length - 1) {
                setCurrentStep(prev => prev + 1);
            }
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
            setError(null);
        }
    };

    // Submit registration
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            // Determine role based on profile type
            const selectedProfileType = profileTypes.find(t => t.code === profileType);
            const role = selectedProfileType?.isProfessional ? 'PRO' : 'PRIVATE';

            // Hash password once with SHA-256 for storage
            const hashedPassword = await encryptPassword(formData.password);

            const peopleData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                sexe: formData.sexe,
                email: formData.email,
                phone: convertToE164(formData.phone),
                gsm: convertToE164(formData.gsm),           // Téléphone mobile
                phoneBureau: convertToE164(formData.phoneBureau), // Téléphone bureau
                company: formData.company,
                position: formData.position,
                profession: formData.profession,            // Métier/titre professionnel
                address: formData.address,
                googleAddress: formData.address, // Store address as googleAddress in peoples
                street: formData.street,
                number: formData.number,
                box: formData.box,
                postalCode: formData.zip,                   // Map zip to postalCode for DB
                city: formData.city,
                country: formData.country,
                countryCode: formData.countryCode,
                latitude: formData.latitude,
                longitude: formData.longitude,
                googlePlaceId: formData.googlePlaceId,
                googleUrl: formData.googleUrl,
                nationality: formData.nationality,
                website: formData.website,
                description: formData.description,
                encryptedPassword: hashedPassword, // Simple SHA-256 hash
                role: role,
                profileTypeId: profileTypeId, // Database ID for profile type
                employmentStatus: employmentStatus, // JOBSEEKER, EMPLOYER, or null
                tvaNum: formData.vatNumber, // Map vatNumber to tvaNum for DB
                firmType: formData.firmType,
                companyCountry: formData.companyCountry,
                companyAddress: formData.companyAddress, // googleAddress for the company/place
                showPhone: formData.showPhone ? 1 : 0,
                showGsm: formData.showGsm ? 1 : 0,
                showEmail: formData.showEmail ? 1 : 0
            };

            const result = await createPeople(peopleData);

            // Handle both uppercase (ColdFusion) and lowercase keys
            const success = result.success || result.SUCCESS;
            const data = result.data || result.DATA || {};
            const peopleId = result.peopleId || data.ID || data.id;
            const uuid = result.uuid || result.peopleUuid || data.PEOPLEUUID || data.peopleUuid;

            if (success || peopleId || uuid) {
                // Store user data
                localStorage.setItem('userId', peopleId);
                localStorage.setItem('userUuid', uuid);
                localStorage.setItem('userEmail', formData.email);
                localStorage.setItem('userName', `${formData.firstName} ${formData.lastName}`);

                setCreatedUser(result);

                // If company + tvaNum are filled, create a place (enterprise)
                if (formData.company?.trim() && formData.vatNumber?.trim()) {
                    try {
                        const placeData = {
                            placeName: formData.company,
                            tvaNum: formData.vatNumber,
                            firmType: formData.firmType,
                            googleAddress: formData.companyAddress || formData.address,
                            latitude: formData.latitude,
                            longitude: formData.longitude,
                            email: formData.email,
                            phone: convertToE164(formData.phoneBureau) || convertToE164(formData.phone),
                            website: formData.website,
                            contactName: `${formData.firstName} ${formData.lastName}`,
                            placeType: 'enterprise',
                            createdById: peopleId
                        };
                        await createPlace(placeData);
                        console.log('Place created for company:', formData.company);
                    } catch (placeError) {
                        // Don't block registration if place creation fails
                        console.error('Error creating place:', placeError);
                    }
                }

                // Handle plan selection
                if (selectedPlan === 'free') {
                    // Free plan - show success screen with login button
                    setShowSuccessScreen(true);
                } else {
                    // Paid plan - redirect to payment
                    handlePayment();
                }
            } else {
                const message = result.message || result.MESSAGE || '';
                // Check if email already exists
                if (message.toLowerCase().includes('email already exists')) {
                    setShowEmailExistsModal(true);
                } else {
                    setError(message || t.register?.errors?.accountCreationError || 'Erreur lors de la création du compte');
                }
            }
        } catch (err) {
            console.error('Registration error:', err);
            const errorMessage = err.message || '';
            // Check if email already exists in error
            if (errorMessage.toLowerCase().includes('email already exists')) {
                setShowEmailExistsModal(true);
            } else {
                setError(errorMessage || t.register?.errors?.genericError || 'Une erreur est survenue');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle payment
    const handlePayment = async () => {
        setProcessingPayment(true);
        try {
            const userUuid = localStorage.getItem('userUuid');
            const result = await createPayment(userUuid, selectedPlan);

            if (result.success && result.data?.paymentUrl) {
                window.location.href = result.data.paymentUrl;
            } else {
                setError(result.message || t.register?.errors?.paymentError || 'Erreur de paiement');
            }
        } catch (err) {
            setError(err.message || t.register?.errors?.paymentError || 'Erreur de paiement');
        } finally {
            setProcessingPayment(false);
        }
    };

    // Calculate progress based on visible steps
    const visibleStepsCount = profileType === 'professional' ? STEPS.length : STEPS.filter(s => !s.professionalOnly).length;
    const progress = ((currentStep + 1) / visibleStepsCount) * 100;

    // Render step content
    const renderStepContent = () => {
        const visibleSteps = getVisibleSteps();
        const currentVisibleStep = visibleSteps[currentStep];

        if (!currentVisibleStep) return null;

        switch (currentVisibleStep.id) {
            case 'profile':
                return renderProfileTypeStep();
            case 'personal':
                return renderPersonalStep();
            case 'professional':
                return renderProfessionalStep();
            case 'address':
                return renderAddressStep();
            case 'privacy':
                return renderPrivacyStep();
            case 'plan':
                return renderPlanStep();
            default:
                return null;
        }
    };

    // Step 1: Profile Type Selection
    const renderProfileTypeStep = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t.register?.profileType?.title || 'Quel type de profil êtes-vous ?'}
                </h2>
                <p className="text-gray-600">
                    {t.register?.profileType?.subtitle || 'Cela nous aidera à personnaliser votre expérience'}
                </p>
            </div>

            {/* Two-column layout: Profile Types + Employment Status */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Profile Types - 2 columns */}
                <div className="lg:col-span-2">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <User className="w-4 h-4 text-cabaza-purple" />
                        {t.register?.steps?.profile || 'Type de profil'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {profileTypes.filter(pt => pt.code !== 'JOBSEEKER').map((type) => {
                            const Icon = type.icon || User;
                            const isSelected = profileType === type.code;

                            return (
                                <button
                                    key={type.code}
                                    onClick={() => {
                                        setProfileType(type.code);
                                        setProfileTypeId(type.dbId || type.id);
                                    }}
                                    className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-left ${
                                        isSelected
                                            ? `${type.borderColor} ${type.bgColor} shadow-lg scale-[1.02]`
                                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                                    }`}
                                >
                                    {/* Selection indicator */}
                                    {isSelected && (
                                        <div className="absolute top-3 right-3">
                                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                                        </div>
                                    )}

                                    {/* Icon */}
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-3`}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>

                                    {/* Title & Description */}
                                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                                        {t.register?.profileType?.[type.code.toLowerCase()] || type.title}
                                    </h3>
                                    <p className="text-xs text-gray-600">
                                        {t.register?.profileType?.[`${type.code.toLowerCase()}Desc`] || type.description}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Employment Status - 1 column */}
                <div className="lg:col-span-1">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-orange-500" />
                        {t.register?.employmentStatus?.title || 'Statut emploi'} <span className="text-xs font-normal text-gray-400">{t.register?.employmentStatus?.optional || '(optionnel)'}</span>
                    </h3>
                    <div className="space-y-3">
                        {EMPLOYMENT_STATUS_OPTIONS.map((status) => {
                            const Icon = status.icon || User;
                            const isSelected = employmentStatus === status.id;

                            return (
                                <button
                                    key={status.id}
                                    onClick={() => {
                                        setEmploymentStatus(isSelected ? '' : status.id);
                                    }}
                                    className={`relative w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                                        isSelected
                                            ? `${status.borderColor} ${status.bgColor} shadow-md`
                                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        {/* Icon */}
                                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${status.color} flex items-center justify-center flex-shrink-0`}>
                                            <Icon className="w-5 h-5 text-white" />
                                        </div>

                                        {/* Label */}
                                        <span className="text-sm font-medium text-gray-900">
                                            {status.label}
                                        </span>

                                        {/* Selection indicator */}
                                        {isSelected && (
                                            <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />
                                        )}
                                    </div>
                                </button>
                            );
                        })}

                    </div>
                </div>
            </div>
        </div>
    );

    // Step 2: Personal Information with OCR
    const renderPersonalStep = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t.register?.personal?.title || 'Vos informations personnelles'}
                </h2>
                <p className="text-gray-600">
                    {t.register?.personal?.subtitle || 'Quelques informations pour créer votre compte'}
                </p>
            </div>

            {/* OCR Quick Import */}
            <div className="p-4 border-2 border-dashed border-cabaza-purple/30 rounded-xl bg-purple-50/50">
                <button
                    onClick={() => setShowOcrModal(true)}
                    className="w-full flex items-center justify-center gap-3 py-3 text-cabaza-purple hover:text-cabaza-pink transition-colors"
                >
                    <Camera className="w-6 h-6" />
                    <span className="font-medium">{t.register?.personal?.scanCard || 'Scanner une carte de visite pour pré-remplir'}</span>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.firstName || 'Prénom'} *
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleChange('firstName', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent ${
                                fieldErrors.firstName ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Jean"
                        />
                    </div>
                    {fieldErrors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>
                    )}
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.lastName || 'Nom'} *
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => handleChange('lastName', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent ${
                                fieldErrors.lastName ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Dupont"
                        />
                    </div>
                    {fieldErrors.lastName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>
                    )}
                </div>

                {/* Sexe */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.gender || 'Gender'}
                    </label>
                    <CustomDropdown
                        value={formData.sexe}
                        onChange={(val) => handleChange('sexe', val)}
                        options={[
                            { value: '', label: t.register?.personal?.genderOptions?.unspecified || 'Unspecified' },
                            { value: 'M', label: t.register?.personal?.genderOptions?.male || 'Male' },
                            { value: 'F', label: t.register?.personal?.genderOptions?.female || 'Female' },
                            { value: 'O', label: t.register?.personal?.genderOptions?.other || 'Other' }
                        ]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent bg-white"
                    />
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.email || 'Email'} *
                    </label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent ${
                                fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="jean.dupont@email.com"
                        />
                    </div>
                    {fieldErrors.email && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                    )}
                </div>

                {/* Phone (fixe) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.phoneFixed || 'Téléphone fixe'}
                    </label>
                    <PhoneInput
                        international
                        defaultCountry="BE"
                        value={formData.phone}
                        onChange={(value) => handleChange('phone', value || '')}
                        className="phone-input-custom"
                    />
                </div>

                {/* GSM (mobile) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.phoneMobile || 'Téléphone mobile'}
                    </label>
                    <PhoneInput
                        international
                        defaultCountry="BE"
                        value={formData.gsm}
                        onChange={(value) => handleChange('gsm', value || '')}
                        className="phone-input-custom"
                    />
                </div>

                {/* Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.password || 'Mot de passe'} *
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => handleChange('password', e.target.value)}
                            className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent ${
                                fieldErrors.password ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t.register?.validation?.passwordMinLength || 'Minimum 6 caractères'}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    {fieldErrors.password && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                    )}
                </div>

                {/* Confirm Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.personal?.confirmPassword || 'Confirmer le mot de passe'} *
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={(e) => handleChange('confirmPassword', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent ${
                                fieldErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t.register?.personal?.confirmPassword || 'Répétez le mot de passe'}
                        />
                    </div>
                    {fieldErrors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
                    )}
                </div>
            </div>

            {/* OCR Modal */}
            {showOcrModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {t.register?.ocr?.modalTitle || 'Scanner une carte de visite'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowOcrModal(false);
                                    clearImage();
                                }}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {imagePreview ? (
                            <div className="space-y-4">
                                <img
                                    src={imagePreview}
                                    alt={t.register?.ocr?.businessCard || 'Carte de visite'}
                                    className="w-full rounded-lg"
                                />
                                {ocrProcessing && (
                                    <div className="flex items-center justify-center gap-2 text-cabaza-purple">
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>{t.register?.ocr?.analyzing || 'Analyse en cours...'}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <label className="block cursor-pointer">
                                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-cabaza-purple transition-colors">
                                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                                    <p className="text-gray-600 mb-2">
                                        {t.register?.ocr?.selectImage || 'Cliquez pour sélectionner une image'}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {t.register?.ocr?.supportedFormats || 'JPG, PNG ou HEIC'}
                                    </p>
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleOcrUpload}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    // Step 3: Professional Information (only for professional profile)
    const renderProfessionalStep = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t.register?.professional?.title || 'Votre entreprise'}
                </h2>
                <p className="text-gray-600">
                    {t.register?.professional?.subtitle || 'Informations sur votre activité professionnelle'}
                </p>
            </div>

            {/* Country Selection */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.register?.professional?.companyCountry || 'Pays de l\'entreprise'} *
                </label>
                <CustomDropdown
                    value={formData.companyCountry}
                    onChange={(val) => {
                        handleChange('companyCountry', val);
                        handleChange('firmType', ''); // Reset firm type when country changes
                        // Reset BCE search when country changes
                        setVatSearchResult(null);
                        setVatError(null);
                        setNameSearchResults([]);
                    }}
                    options={Object.entries(EUROPEAN_COUNTRIES).map(([code, country]) => ({
                        value: code,
                        label: `${country.flag} ${country.name}`
                    }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple"
                />
            </div>

            {/* BCE Search (Belgium only) */}
            {formData.companyCountry === 'BE' && (
                <div className="p-6 bg-purple-50 rounded-2xl">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center gap-2">
                            <span className="text-2xl">🇧🇪</span>
                            <Building2 className="w-6 h-6 text-cabaza-purple" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t.register?.professional?.bceSearch || 'Recherche BCE'}
                        </h3>
                    </div>

                    {/* Search mode tabs */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => {
                                setBceSearchMode('vat');
                                setVatError(null);
                                setNameSearchResults([]);
                                setSelectedBceResult(null);
                            }}
                            className={`flex-1 py-2 px-4 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
                                bceSearchMode === 'vat'
                                    ? 'bg-cabaza-purple text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {t.register?.professional?.byVat || 'Par N° TVA'}
                        </button>
                        <button
                            onClick={() => {
                                setBceSearchMode('name');
                                setVatError(null);
                                setVatSearchResult(null);
                            }}
                            className={`flex-1 py-2 px-4 min-h-11 sm:min-h-0 rounded-lg text-sm font-medium transition-all ${
                                bceSearchMode === 'name'
                                    ? 'bg-cabaza-purple text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {t.register?.professional?.byName || 'Par nom'}
                        </button>
                    </div>

                    {/* Search by VAT number */}
                    {bceSearchMode === 'vat' && (
                        <>
                            <p className="text-sm text-gray-600 mb-4">
                                {t.register?.professional?.vatSearchPlaceholder || 'Entrez votre numéro d\'entreprise BCE pour pré-remplir automatiquement les informations'}
                            </p>

                            <div className="flex gap-3">
                                <div className="flex-1 relative">
                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="BE 0123.456.789"
                                        value={formData.vatNumber}
                                        onChange={(e) => handleChange('vatNumber', e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleVatSearch()}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                    />
                                </div>
                                <button
                                    onClick={handleVatSearch}
                                    disabled={vatSearching}
                                    className="px-6 py-3 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-pink transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {vatSearching ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <SearchIcon className="w-5 h-5" />
                                    )}
                                    {t.common?.search || 'Rechercher'}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Search by company name */}
                    {bceSearchMode === 'name' && (
                        <>
                            <p className="text-sm text-gray-600 mb-4">
                                {t.register?.professional?.searchByName || 'Recherchez votre entreprise par son nom'}
                            </p>

                            <div className="flex gap-3">
                                <div className="flex-1 relative">
                                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={t.register?.professional?.companyNamePlaceholder || 'Nom de l\'entreprise...'}
                                        value={nameSearchQuery}
                                        onChange={(e) => setNameSearchQuery(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleNameSearch()}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                    />
                                </div>
                                <button
                                    onClick={handleNameSearch}
                                    disabled={nameSearching}
                                    className="px-6 py-3 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-pink transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {nameSearching ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <SearchIcon className="w-5 h-5" />
                                    )}
                                    {t.common?.search || 'Rechercher'}
                                </button>
                            </div>

                            {/* Search results list */}
                            {nameSearchResults.length > 0 && (
                                <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm max-h-96 overflow-y-auto">
                                    {nameSearchResults.map((result, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSelectBceResult(result)}
                                            className="w-full p-4 text-left hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center shrink-0">
                                                    <Building2 className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div>
                                                            <p className="font-semibold text-gray-900">
                                                                {result.placeName}
                                                            </p>
                                                            {result.firmType && (
                                                                <p className="text-xs text-cabaza-purple font-medium">
                                                                    {result.firmType}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <ArrowRight className="w-5 h-5 text-cabaza-purple shrink-0" />
                                                    </div>

                                                    <div className="mt-2 space-y-1">
                                                        <p className="text-xs text-gray-600">
                                                            <span className="font-medium">TVA:</span> {result.tvaNum}
                                                        </p>
                                                        {result.googleAddress && (
                                                            <p className="text-xs text-gray-600 flex items-start gap-1">
                                                                <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" />
                                                                <span>{result.googleAddress}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    <p className="mt-2 text-xs text-gray-500">
                        {t.register?.professional?.bceNote || 'Données récupérées via la Banque Carrefour des Entreprises (BCE)'}
                    </p>

                    {vatError && (
                        <p className="mt-3 text-sm text-red-600 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {vatError}
                        </p>
                    )}

                    {vatSearchResult && (
                        <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center shrink-0">
                                    <Building2 className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-gray-900 text-lg truncate">
                                        {vatSearchResult.placeName}
                                    </h4>
                                    {vatSearchResult.firmType && (
                                        <p className="text-sm text-gray-500">{vatSearchResult.firmType}</p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2 mb-4">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-gray-400 w-20">N° TVA:</span>
                                    <span className="font-medium text-gray-900">{vatSearchResult.tvaNum}</span>
                                </div>
                                {vatSearchResult.googleAddress && (
                                    <div className="flex items-start gap-2 text-sm">
                                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                                        <span className="text-gray-700">{vatSearchResult.googleAddress}</span>
                                    </div>
                                )}
                                {vatSearchResult.phone && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-700">{vatSearchResult.phone}</span>
                                    </div>
                                )}
                                {vatSearchResult.website && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Globe className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-700">{vatSearchResult.website}</span>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => setShowBceConfirmModal(true)}
                                className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2 font-medium shadow-sm"
                            >
                                <CheckCircle2 className="w-5 h-5" />
                                {t.register?.ocr?.useInfo || 'Utiliser ces informations'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Professional fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Firm Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.firmType || 'Type d\'entreprise'} *
                    </label>
                    <CustomDropdown
                        value={formData.firmType}
                        onChange={(val) => handleChange('firmType', val)}
                        options={[
                            { value: '', label: t.common?.selectOption || 'Sélectionnez un type...' },
                            ...(EUROPEAN_COUNTRIES[formData.companyCountry]?.types || []).map((type) => ({
                                value: type.label,
                                label: type.label
                            }))
                        ]}
                        className={`w-full px-4 py-3 border rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple ${
                            fieldErrors.firmType ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {fieldErrors.firmType && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.firmType}</p>
                    )}
                </div>

                {/* Company */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.companyName || 'Nom de l\'entreprise'} *
                    </label>
                    <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.company}
                            onChange={(e) => handleChange('company', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent ${
                                fieldErrors.company ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t.register?.professional?.companyName || 'Nom de l\'entreprise'}
                        />
                    </div>
                    {fieldErrors.company && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.company}</p>
                    )}
                </div>

                {/* VAT Number */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.vatNumber || 'N° TVA / N° Entreprise'}
                    </label>
                    <div className="relative">
                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.vatNumber}
                            onChange={(e) => handleChange('vatNumber', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder={formData.companyCountry === 'BE' ? 'BE 0123.456.789' : formData.companyCountry === 'FR' ? 'FR XX 123 456 789' : 'Numéro TVA'}
                        />
                    </div>
                </div>

                {/* Position */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.position || 'Votre fonction'}
                    </label>
                    <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.position}
                            onChange={(e) => handleChange('position', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder="Ex: Directeur, Manager, etc."
                        />
                    </div>
                </div>

                {/* Profession */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.profession || 'Profession / Métier'}
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.profession}
                            onChange={(e) => handleChange('profession', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder="Ex: Architecte, Avocat, etc."
                        />
                    </div>
                </div>

                {/* Phone Bureau */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.phoneProfessional || 'Téléphone professionnel'}
                    </label>
                    <PhoneInput
                        international
                        defaultCountry="BE"
                        value={formData.phoneBureau}
                        onChange={(value) => handleChange('phoneBureau', value || '')}
                        className="phone-input-custom"
                    />
                </div>

                {/* Company Address */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.companyAddress || 'Adresse de l\'entreprise'}
                    </label>
                    <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={formData.companyAddress}
                            onChange={(e) => handleChange('companyAddress', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder={t.register?.professional?.companyAddressPlaceholder || 'Adresse complète de l\'entreprise'}
                        />
                    </div>
                </div>

                {/* Website */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.register?.professional?.website || 'Site web'}
                    </label>
                    <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => handleChange('website', e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder="https://www.monentreprise.be"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    // Step 4: Address
    const renderAddressStep = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t.register?.address?.title || 'Votre localisation'}
                </h2>
                <p className="text-gray-600">
                    {t.register?.address?.subtitle || 'Allow other members to find you'}
                </p>
            </div>

            <div className="max-w-2xl mx-auto space-y-4">
                {/* Address Autocomplete */}
                <AddressAutocomplete
                    label={t.register?.address?.searchAddress || 'Rechercher une adresse'}
                    value={formData.address}
                    onChange={handleAddressChange}
                    onCoordinatesChange={handleCoordinatesChange}
                    onAddressComponentsChange={handleAddressComponentsChange}
                    apiKey={googleMapsApiKey}
                />

                {/* Detailed address fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.street || 'Rue'}
                        </label>
                        <input
                            type="text"
                            value={formData.street}
                            onChange={(e) => handleChange('street', e.target.value)}
                            placeholder={t.register?.address?.streetPlaceholder || 'Nom de la rue'}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.number || 'N°'}
                        </label>
                        <input
                            type="text"
                            value={formData.number}
                            onChange={(e) => handleChange('number', e.target.value)}
                            placeholder="123"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.box || 'Boîte'}
                        </label>
                        <input
                            type="text"
                            value={formData.box}
                            onChange={(e) => handleChange('box', e.target.value)}
                            placeholder="A, B..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.postalCode || 'Code postal'}
                        </label>
                        <input
                            type="text"
                            value={formData.zip}
                            onChange={(e) => handleChange('zip', e.target.value)}
                            placeholder="1000"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.city || 'Ville'}
                        </label>
                        <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => handleChange('city', e.target.value)}
                            placeholder="Bruxelles"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.country || 'Pays'}
                        </label>
                        <input
                            type="text"
                            value={formData.country}
                            onChange={(e) => handleChange('country', e.target.value)}
                            placeholder="Belgique"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.register?.address?.nationality || 'Nationalité'}
                        </label>
                        <input
                            type="text"
                            value={formData.nationality}
                            onChange={(e) => handleChange('nationality', e.target.value)}
                            placeholder="Belge, Français..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                </div>

                <p className="text-sm text-gray-500 text-center mt-4">
                    <Home className="w-4 h-4 inline mr-1" />
                    {t.register?.address?.privacyNote || 'Your address is only used for our administration or if you respond to an address request from a Cabaza profile, otherwise only the municipality where you live will be shown on the map'}
                </p>
            </div>
        </div>
    );

    // Step 5: Privacy Settings
    const renderPrivacyStep = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t.register?.privacy?.title || 'Paramètres de confidentialité'}
                </h2>
                <p className="text-gray-600">
                    {t.register?.privacy?.subtitle || 'Choisissez les informations visibles sur votre profil public'}
                </p>
            </div>

            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center">
                        <Settings className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">
                            {t.register?.privacy?.visibilityTitle || 'Visibilité du profil'}
                        </h3>
                        <p className="text-xs text-gray-500">
                            {t.register?.privacy?.visibilitySubtitle || 'Ces paramètres contrôlent ce que les autres utilisateurs voient'}
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    {/* Show Phone toggle */}
                    <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Phone className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                {t.register?.privacy?.showPhone || 'Montrer mon téléphone'}
                            </span>
                        </div>
                        <div
                            className={`relative w-11 h-6 rounded-full transition-colors ${formData.showPhone ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                            onClick={() => handleChange('showPhone', !formData.showPhone)}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.showPhone ? 'translate-x-5' : 'translate-x-0'}`}></div>
                        </div>
                    </label>

                    {/* Show GSM toggle */}
                    <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                                <Smartphone className="w-4 h-4 text-green-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                {t.register?.privacy?.showGsm || 'Montrer mon GSM'}
                            </span>
                        </div>
                        <div
                            className={`relative w-11 h-6 rounded-full transition-colors ${formData.showGsm ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                            onClick={() => handleChange('showGsm', !formData.showGsm)}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.showGsm ? 'translate-x-5' : 'translate-x-0'}`}></div>
                        </div>
                    </label>

                    {/* Show Email toggle */}
                    <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <Mail className="w-4 h-4 text-purple-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                {t.register?.privacy?.showEmail || 'Montrer mon e-mail'}
                            </span>
                        </div>
                        <div
                            className={`relative w-11 h-6 rounded-full transition-colors ${formData.showEmail ? 'bg-cabaza-purple' : 'bg-gray-300'}`}
                            onClick={() => handleChange('showEmail', !formData.showEmail)}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.showEmail ? 'translate-x-5' : 'translate-x-0'}`}></div>
                        </div>
                    </label>
                </div>

                <p className="mt-5 text-xs text-gray-400 text-center">
                    {t.register?.privacy?.note || 'Vous pourrez modifier ces paramètres à tout moment depuis votre profil.'}
                </p>
            </div>
        </div>
    );

    // Step 6: Plan Selection
    const renderPlanStep = () => {
        // If still loading tarifs, show loader
        if (loadingTarifs) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-cabaza-purple animate-spin" />
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {t.register?.plan?.title || 'Choisissez votre formule'}
                    </h2>
                    <p className="text-gray-600">
                        {t.register?.plan?.subtitle || 'Vous pouvez toujours changer d\'avis plus tard'}
                    </p>
                </div>

                {/* Plan cards from API */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {tarifs.map((tarif) => {
                        const tarifCode = (tarif.code || '').toLowerCase();
                        const isSelected = selectedPlan === tarifCode;
                        const monthlyPrice = tarif.finalmonthlyprice ?? tarif.monthlyprice ?? 0;
                        const tarifName = tarif[`name_${language}`] || tarif.name_fr || tarif.name || tarif.code;
                        const tarifDescription = tarif[`description_${language}`] || tarif.description_fr || tarif.description || '';
                        const isPopular = tarif.isrecommended || tarif.isRecommended || false;
                        const modulesForTarif = tarifModules[tarifCode] || [];

                        return (
                            <div
                                key={tarif.id}
                                onClick={() => setSelectedPlan(tarifCode)}
                                className={`relative rounded-xl p-6 cursor-pointer transition-all ${
                                    isSelected
                                        ? 'border-2 border-cabaza-purple shadow-lg scale-105'
                                        : 'border-2 border-gray-200 hover:border-cabaza-purple/50'
                                } ${isPopular ? 'bg-gradient-to-br from-cabaza-purple/5 to-cabaza-pink/5' : 'bg-white'}`}
                            >
                                {isPopular && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                        <span className="inline-flex items-center gap-1 bg-cabaza-purple text-white px-3 py-1 rounded-full text-xs font-semibold">
                                            <Sparkles className="w-3 h-3" />
                                            {t.register?.plan?.recommended || 'Recommandé'}
                                        </span>
                                    </div>
                                )}

                                <div className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                    isSelected
                                        ? 'border-cabaza-purple bg-cabaza-purple'
                                        : 'border-gray-300'
                                }`}>
                                    {isSelected && (
                                        <Check className="w-4 h-4 text-white" />
                                    )}
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-1">{tarifName}</h3>
                                {tarifDescription && (
                                    <p className="text-sm text-gray-500 mb-4">{tarifDescription}</p>
                                )}

                                <div className="mb-4">
                                    <span className="text-3xl font-bold text-gray-900">{monthlyPrice}€</span>
                                    <span className="text-gray-500">{t.register?.plan?.perMonth || '/mois'}</span>
                                </div>

                                {/* Modules included */}
                                {modulesForTarif.length > 0 && (
                                    <ul className="space-y-2">
                                        {modulesForTarif.slice(0, 5).map((mod) => {
                                            const modName = mod[`name_${language}`] || mod.name_fr || mod.name || mod.code;
                                            return (
                                                <li key={mod.id || mod.moduleid} className="flex items-center gap-2 text-sm text-gray-600">
                                                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                                                    {modName}
                                                </li>
                                            );
                                        })}
                                        {modulesForTarif.length > 5 && (
                                            <li className="text-sm text-gray-400 italic pl-6">
                                                +{modulesForTarif.length - 5} {t.register?.plan?.moreModules || 'autres modules'}
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Payment methods */}
                {selectedPlan !== 'free' && (
                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500 mb-3">{t.register?.plan?.paymentMethods || 'Moyens de paiement acceptés'}</p>
                        <div className="flex items-center justify-center gap-4">
                            <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-6" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Credit Card" className="h-6" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-6" />
                            <img src="https://www.mollie.com/external/icons/payment-methods/paypal.svg" alt="PayPal" className="h-6" />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 flex flex-col">
            <Header onLogin={onLogin} />

            <main className="flex-1 py-8">
                <div className="max-w-4xl mx-auto px-4">
                    {/* Progress bar */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">
                                {(t.register?.stepOf || 'Étape {current} sur {total}').replace('{current}', currentStep + 1).replace('{total}', visibleStepsCount)}
                            </span>
                            <span className="text-sm font-medium text-cabaza-purple">
                                {Math.round(progress)}%
                            </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-cabaza-purple to-cabaza-pink transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>

                        {/* Step indicators */}
                        <div className="flex justify-between mt-4">
                            {getVisibleSteps().map((step, index) => {
                                const Icon = step.icon;
                                const isCompleted = index < currentStep;
                                const isCurrent = index === currentStep;

                                return (
                                    <div
                                        key={step.id}
                                        className={`flex flex-col items-center ${
                                            index <= currentStep ? 'text-cabaza-purple' : 'text-gray-400'
                                        }`}
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-all ${
                                            isCompleted
                                                ? 'bg-green-500 text-white'
                                                : isCurrent
                                                    ? 'bg-cabaza-purple text-white'
                                                    : 'bg-gray-200 text-gray-400'
                                        }`}>
                                            {isCompleted ? (
                                                <Check className="w-5 h-5" />
                                            ) : (
                                                <Icon className="w-5 h-5" />
                                            )}
                                        </div>
                                        <span className="text-xs hidden sm:block">{step.title}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Step content */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
                        {renderStepContent()}
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex items-center justify-between">
                        <button
                            onClick={currentStep === 0 ? () => navigate('/') : prevStep}
                            className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            {currentStep === 0 ? (t.register?.navigation?.back || 'Retour') : (t.register?.navigation?.previous || 'Précédent')}
                        </button>

                        {currentStep < visibleStepsCount - 1 ? (
                            <button
                                onClick={nextStep}
                                disabled={isValidating}
                                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                            >
                                {isValidating ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        {t.register?.navigation?.validating || 'Vérification...'}
                                    </>
                                ) : (
                                    <>
                                        {t.register?.navigation?.next || 'Suivant'}
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || processingPayment}
                                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                            >
                                {isSubmitting || processingPayment ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        {processingPayment ? (t.register?.navigation?.redirecting || 'Redirection...') : (t.register?.navigation?.creating || 'Création...')}
                                    </>
                                ) : (
                                    <>
                                        {selectedPlan === 'free' ? (
                                            <>
                                                {t.register?.navigation?.startFree || 'Commencer gratuitement'}
                                                <ArrowRight className="w-5 h-5" />
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="w-5 h-5" />
                                                {(t.register?.navigation?.pay || 'Payer {price}€').replace('{price}', plans.find(p => p.id === selectedPlan)?.price)}
                                            </>
                                        )}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </main>

            <Footer />

            {/* OCR Confirmation Modal */}
            {showOcrConfirmModal && ocrExtractedData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Camera className="w-5 h-5 text-cabaza-purple" />
                                {t.register?.ocr?.detectedInfo || 'Informations détectées'}
                            </h3>
                            <button
                                onClick={cancelOcrData}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            {t.register?.ocr?.confirmMessage || 'Voici les informations extraites de la carte de visite. Voulez-vous les utiliser pour pré-remplir le formulaire ?'}
                        </p>

                        <div className="space-y-3 mb-6">
                            {ocrExtractedData.firstName && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Prénom:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.firstName}</span>
                                </div>
                            )}
                            {ocrExtractedData.lastName && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Nom:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.lastName}</span>
                                </div>
                            )}
                            {ocrExtractedData.email && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <Mail className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Email:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.email}</span>
                                </div>
                            )}
                            {ocrExtractedData.phone && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Téléphone:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.phone}</span>
                                </div>
                            )}
                            {ocrExtractedData.company && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <Building2 className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Entreprise:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.company}</span>
                                </div>
                            )}
                            {ocrExtractedData.position && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <Briefcase className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Fonction:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.position}</span>
                                </div>
                            )}
                            {ocrExtractedData.website && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <Globe className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Site web:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.website}</span>
                                </div>
                            )}
                            {ocrExtractedData.address && (
                                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">Adresse:</span>
                                    <span className="text-sm font-medium text-gray-900">{ocrExtractedData.address}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={cancelOcrData}
                                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t.common?.cancel || 'Annuler'}
                            </button>
                            <button
                                onClick={applyOcrData}
                                className="flex-1 py-3 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-pink transition-colors flex items-center justify-center gap-2"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                {t.register?.ocr?.useInfo || 'Utiliser ces informations'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BCE Confirmation Modal */}
            {showBceConfirmModal && bceDataToApply && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-cabaza-purple" />
                                {t.register?.bce?.confirmTitle || 'Confirmer les informations BCE'}
                            </h3>
                            <button
                                onClick={cancelBceData}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            {t.register?.bce?.confirmMessage || 'Les informations suivantes vont être utilisées pour pré-remplir votre profil. Voulez-vous continuer ?'}
                        </p>

                        <div className="space-y-3 mb-6">
                            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                                <Building2 className="w-5 h-5 text-cabaza-purple" />
                                <div>
                                    <span className="text-xs text-gray-500">{t.register?.bce?.company || 'Entreprise'}</span>
                                    <p className="font-semibold text-gray-900">{bceDataToApply.placeName}</p>
                                </div>
                            </div>

                            {bceDataToApply.googleAddress && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <MapPin className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <span className="text-xs text-gray-500">{t.register?.bce?.address || 'Adresse'}</span>
                                        <p className="font-medium text-gray-900">{bceDataToApply.googleAddress}</p>
                                    </div>
                                </div>
                            )}

                            {bceDataToApply.tvaNum && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <CreditCard className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <span className="text-xs text-gray-500">{t.register?.bce?.vatNumber || 'N° TVA'}</span>
                                        <p className="font-medium text-gray-900">{bceDataToApply.tvaNum}</p>
                                    </div>
                                </div>
                            )}

                            {bceDataToApply.firmType && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <Briefcase className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <span className="text-xs text-gray-500">{t.register?.bce?.firmType || 'Type d\'entreprise'}</span>
                                        <p className="font-medium text-gray-900">{bceDataToApply.firmType}</p>
                                    </div>
                                </div>
                            )}

                            {bceDataToApply.phone && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <Phone className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <span className="text-xs text-gray-500">{t.register?.bce?.phone || 'Téléphone'}</span>
                                        <p className="font-medium text-gray-900">{bceDataToApply.phone}</p>
                                    </div>
                                </div>
                            )}

                            {bceDataToApply.website && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <Globe className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <span className="text-xs text-gray-500">{t.register?.bce?.website || 'Site web'}</span>
                                        <p className="font-medium text-gray-900">{bceDataToApply.website}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                            <p className="text-sm text-amber-800">
                                <AlertCircle className="w-4 h-4 inline mr-1" />
                                {t.register?.bce?.warning || 'Les informations existantes dans le formulaire seront remplacées par ces données.'}
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={cancelBceData}
                                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t.common?.cancel || 'Annuler'}
                            </button>
                            <button
                                onClick={applyBceData}
                                className="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                {t.register?.bce?.confirmAndContinue || 'Confirmer et continuer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Already Exists Modal */}
            {showEmailExistsModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                                <Mail className="w-8 h-8 text-amber-600" />
                            </div>
                        </div>

                        <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                            {t.register?.emailExists?.title || 'Compte existant'}
                        </h3>

                        <p className="text-gray-600 text-center mb-6">
                            {t.register?.emailExists?.message || 'Un compte existe déjà avec l\'adresse email'} <span className="font-semibold text-cabaza-purple">{formData.email}</span>. {t.register?.emailExists?.loginQuestion || 'Souhaitez-vous vous connecter ?'}
                        </p>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => navigate('/login', { state: { email: formData.email } })}
                                className="w-full py-3 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 font-medium"
                            >
                                <ArrowRight className="w-4 h-4" />
                                {t.register?.emailExists?.login || 'Se connecter'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowEmailExistsModal(false);
                                    setCurrentStep(1); // Go back to personal info step
                                }}
                                className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t.register?.emailExists?.useOtherEmail || 'Utiliser une autre adresse email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Screen - Profile Created */}
            {showSuccessScreen && (
                <div className="fixed inset-0 bg-gradient-to-br from-cabaza-purple/10 to-cabaza-pink/10 flex items-center justify-center z-[1000] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                        <div className="flex items-center justify-center mb-6">
                            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle2 className="w-10 h-10 text-green-600" />
                            </div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {t.register?.success?.title || 'Profil créé avec succès !'}
                        </h2>

                        <p className="text-gray-600 mb-2">
                            {t.register?.success?.subtitle || 'Votre compte a été créé. Vous pouvez maintenant vous connecter.'}
                        </p>

                        <p className="text-sm text-gray-500 mb-8">
                            {t.register?.success?.emailSent || 'Un email de confirmation a été envoyé à'} <span className="font-semibold text-cabaza-purple">{formData.email}</span>
                        </p>

                        <button
                            onClick={() => navigate('/login', { state: { email: formData.email } })}
                            className="w-full py-4 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 font-semibold text-lg"
                        >
                            <ArrowRight className="w-5 h-5" />
                            {t.register?.success?.loginButton || 'Se connecter'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterPage;
